import { getDatasetSafe, getDefinitiveNode, getParsedArrayDatasetSafe, hasElement, safeCallback, stringToDom } from "@nx-paeonia/utils-dom";
import { progressInstance } from "@nx-paeonia/utils-events";
import { err, ok, type Result } from "@nx-paeonia/utils-result";
import { resolveShopifyUrl } from "@nx-paeonia/utils-shopify";
import { animate, stagger } from "motion";
import { createCardMessageApp } from "../../../vue-apps/card-message/main.js";
import { createPickACardApp } from "../../../vue-apps/pick-a-card/main.js";
import { defineSelectCardComponent } from "./components/select-card.component.js";

type SearchView = "cards-swup-card" | "cards-swup-nocard";
type Error = { errorCode: number; errorMessage: string };

function shouldLoadNewApps(): boolean {
	return !document.querySelector("y-card-lint-selector");
}

function featureFlagDefinedInit() {
	if (shouldLoadNewApps()) {
		createPickACardApp();
		createCardMessageApp();
	} else {
		defineSelectCardComponent();

		const rootElement = getDefinitiveNode<HTMLFieldSetElement>("y-select-card");
		const searchView = getDatasetSafe<SearchView>(rootElement, "searchView", "cards-swup-card");

		document.addEventListener("filter-change", () => {
			asyncReplaceContent(searchView);
		});

		asyncReplaceContent(searchView);
	}
}

/**
 * Initialize the card selection part of the configurator.
 *
 * note: Check if this works with refreshes in implementation.
 */
export function initCardConfigurator() {
	featureFlagDefinedInit();
}

/**
 * Fetch products by their IDs using the search endpoint.
 *
 * @deprecated - This will be replaced by a vue app in a future release.
 *  NOTE: It will take some more effort as the lint/card selector still uses this.
 */
async function getProductsByIds<T extends Array<string>>(ids: T, searchView: SearchView): Promise<Result<string, Error>> {
	const search = ids.map((id) => `id:${id}`).join(" OR ");
	const searchParams = new URLSearchParams();
	searchParams.set("type", "product");
	searchParams.set("view", searchView);
	searchParams.set("q", search);

	const url = resolveShopifyUrl("search");

	const response = await fetch(`${url}?${searchParams.toString()}`);
	if (!response.ok) {
		return err({ errorMessage: "Failed to fetch products", errorCode: response.status });
	}

	return ok(await response.text());
}

/**
 * Asynchronously replaces the content of a card container with products filtered by selected IDs.
 *
 * @param searchView - The SearchView instance used to fetch products
 * @returns A Promise that resolves when the content replacement is complete, or null if the operation fails
 *
 * @remarks
 * This function:
 * - Finds the currently checked card configurator filter
 * - Extracts product IDs from the filter's dataset
 * - Fetches products matching those IDs using the provided SearchView
 * - Transforms the result into DOM elements
 * - Sorts the products based on the order of the provided IDs by setting an order style on each label.
 * - Sets selected for the first product in the list
 * - Replaces the card container content with the fetched products if successful
 *
 * The operation is wrapped in a safe callback to handle potential errors gracefully.
 *
 * @deprecated - This will be replaced with the vue app in a future release.
 */
function asyncReplaceContent(searchView: SearchView): Promise<void> | null {
	return safeCallback(async () => {
		progressInstance.start();
		// toggleLoadingState(true);
		animateCards("out");

		const selectedFilter = getDefinitiveNode<HTMLInputElement>(".js-card-configurator-filter:checked");
		const ids = getParsedArrayDatasetSafe<Array<string>>(selectedFilter, "ids", []);
		const sectionWrapper = getDefinitiveNode<HTMLElement>("#swup-card-container");

		const result = await getProductsByIds(ids, searchView);

		if (result.success) {
			const vDom = stringToDom(result.data);
			const products = vDom.querySelectorAll<HTMLElement>("label[data-product-id]");

			products.forEach((product) => {
				const productId = getDatasetSafe<string>(product, "productId", "no-card");
				if (productId === "no-card") {
					product.style.order = "999";
				} else {
					const orderIndex = ids.indexOf(productId);
					product.style.order = orderIndex !== -1 ? orderIndex.toString() : products.length.toString();

					getDefinitiveNode<HTMLInputElement>("input", product).checked = orderIndex === 0;
				}
			});

			// Start copying over additional attributes if we have those
			sectionWrapper.getAttributeNames().forEach((attributeName) => {
				if (!vDom.firstElementChild?.hasAttribute(attributeName)) {
					vDom.firstElementChild?.setAttribute(attributeName, sectionWrapper.getAttribute(attributeName) || "");
				}
			});

			sectionWrapper.replaceWith(vDom);
		}

		animateCards("in");
		// toggleLoadingState(false);
		progressInstance.done();
	});
}

/**
 * Triggers animations for card elements based on the specified direction.
 * @param direction - A string indicating the animation direction, either 'in' or 'out'.
 *
 * @deprecated - This will be removed by a vue app in a future release.
 */
function animateCards(direction: "in" | "out") {
	if (!hasElement("#swup-card-container label")) return;
	const elements = Array.from(document.querySelectorAll("#swup-card-container label"))?.sort((a, b) => {
		return parseInt(getComputedStyle(a).order) - parseInt(getComputedStyle(b).order);
	});
	if (direction === "in") {
		animate(elements, { opacity: [0, 1], transform: ["translateY(20px)", "translateY(0)"] }, { delay: stagger(0.1) });
	} else {
		animate(elements, { opacity: [1, 0], transform: ["translateY(0)", "translateY(20px)"] }, { delay: stagger(0.1) });
	}
}
